import { request } from "./client";
import { registerMessages } from "./errors";

registerMessages({
  "failed to load base fee": "Не удалось загрузить базовую цену",
  "failed to load history": "Не удалось загрузить историю цен",
  "monthly_fee must be > 0": "Укажите цену больше нуля",
  "effective_from must be YYYY-MM-DD": "Некорректная дата начала",
  "failed to create base fee": "Не удалось сохранить базовую цену",
  "id required": "Не указано, какую цену удалить",
  "failed to delete base fee": "Не удалось удалить базовую цену",
  "only future base fees can be deleted": "Эта цена уже вступила в силу — удалить её нельзя",
  "failed to load group fees": "Не удалось загрузить цены групп",
  "failed to load future group fees": "Не удалось загрузить запланированные цены групп",
  "group_id required": "Не выбрана группа",
  "failed to create override": "Не удалось сохранить цену группы",
  "failed to delete group fee": "Не удалось удалить цену группы",
  "only future group fees can be deleted": "Эта цена уже вступила в силу — удалить её нельзя",
});

export interface BaseFee {
  id: string;
  monthly_fee: number;
  effective_from: string;
  created_by?: string;
  created_at: string;
}

export interface GroupFeeEntry {
  id: string;
  group_id: string;
  monthly_fee: number;
  effective_from: string;
  created_at: string;
}

export interface GroupCurrentFee {
  group_id: string;
  group_name: string;
  has_override: boolean;
  monthly_fee?: number;
  effective_from?: string;
  fee_created_at?: string;
}

const BASE = "/api/payment-settings";

// Without effective_from the server starts the price today (by UTC).
const priceBody = (monthlyFee: number, effectiveFrom?: string) =>
  effectiveFrom ? { monthly_fee: monthlyFee, effective_from: effectiveFrom } : { monthly_fee: monthlyFee };

export const feesApi = {
  getBaseFee: async () => (await request<{ current: BaseFee | null }>(`${BASE}/base-fee`)).current ?? null,

  listBaseFeeHistory: () => request<BaseFee[] | null>(`${BASE}/base-fee/history`),

  // The server upserts by date: a second price on the same date replaces the first.
  setBaseFee: (monthlyFee: number, effectiveFrom?: string) =>
    request<BaseFee>(`${BASE}/base-fee`, {
      method: "POST",
      body: JSON.stringify(priceBody(monthlyFee, effectiveFrom)),
    }),

  deleteFutureBaseFee: (id: string) =>
    request<void>(`${BASE}/base-fee/${encodeURIComponent(id)}`, { method: "DELETE" }),

  listGroupFees: () => request<GroupCurrentFee[] | null>(`${BASE}/group-fees`),

  listFutureGroupFees: () => request<GroupFeeEntry[] | null>(`${BASE}/group-fees/futures`),

  setGroupFee: (groupID: string, monthlyFee: number, effectiveFrom?: string) =>
    request<GroupFeeEntry>(`${BASE}/group-fees`, {
      method: "POST",
      body: JSON.stringify({ group_id: groupID, ...priceBody(monthlyFee, effectiveFrom) }),
    }),

  deleteFutureGroupFee: (id: string) =>
    request<void>(`${BASE}/group-fees/${encodeURIComponent(id)}`, { method: "DELETE" }),

  listGroupFeeHistory: (groupID: string) =>
    request<GroupFeeEntry[] | null>(`${BASE}/group-fees/${encodeURIComponent(groupID)}/history`),
};

// Amounts are rubles (NUMERIC(10,2) on the server), not kopecks.
export function formatRubles(amount: number): string {
  return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(amount)} ₽`;
}

// "2026-09-01" → "01.09.2026", from the string: new Date() would shift the day
// west of Greenwich.
export function formatDay(value: string | null | undefined): string {
  if (!value) return "—";
  const [year, month, day] = value.slice(0, 10).split("-");
  return year && month && day ? `${day}.${month}.${year}` : value;
}

export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
